import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, RotateCcw, ShieldAlert, ShieldCheck, Sparkles } from "lucide-react";
import { realtime } from "@/lib/realtime";

/**
 * Session 41.5 — Inventory → "When service stock runs out".
 *
 * Every ingredient whose fallback policy is currently an OVERRIDE of the
 * business default — most importantly the ones a manager flipped to
 * "Service stock first, then fallback" via the Approvals "Approve" shortcut
 * during a rush. At the end of the day the manager resets them here so
 * tomorrow starts on the business default again.
 *
 *   • "Reset" per row       → back to business default (clears the override)
 *   • "Reset all from approvals" → only the approval-driven ones; a policy the
 *                              merchant set deliberately on an ingredient stays.
 */
const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/\.?0+$/, "")));
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || fb; };
const when = (iso) => { if (!iso) return ""; try { const d = new Date(iso); return `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`; } catch (_) { return ""; } };

const POLICY_TONE = {
  auto: "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300",
  manager: "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300",
  none: "bg-destructive/10 text-destructive hover:bg-destructive/10",
};
const POLICY_SHORT = { auto: "Service stock first, then fallback", manager: "Require manager approval", none: "Do not fallback", shortfall: "Business default" };

export const FallbackOverridesPanel = ({ http, authHeaders, canManage = true, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);
  const [filter, setFilter] = useState("all"); // all | approval | manual

  const load = useCallback(async () => {
    setError("");
    try { const r = await http.get("/owner/ingredients/fallback-overrides", { headers: authHeaders }); setData(r.data || { overrides: [] }); }
    catch (e) { setError(errMsg(e, "Couldn't load the override list.")); setData((d) => d || { overrides: [] }); }
  }, [http, authHeaders]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const unsub = realtime.subscribe((msg) => { if (/^(ingredient_policy_changed|fallback_approval_decided)$/.test(String(msg?.type || ""))) load(); });
    const iv = setInterval(load, 30000);
    return () => { unsub(); clearInterval(iv); };
  }, [load]);

  const rows = useMemo(() => {
    const all = data?.overrides || [];
    if (filter === "approval") return all.filter((r) => r.reason === "approval");
    if (filter === "manual") return all.filter((r) => r.reason !== "approval");
    return all;
  }, [data, filter]);
  const approvalCount = data?.approval_count || 0;

  const reset = async (ids, label) => {
    setBusy(ids ? ids.join(",") : "all");
    try {
      const r = await http.post("/owner/ingredients/fallback-overrides/reset", { ingredient_ids: ids || null }, { headers: authHeaders });
      const n = r.data?.reset || 0;
      toast.success(n ? `${label || "Reset"} — ${n} ingredient${n === 1 ? "" : "s"} back to the business default.` : "Nothing to reset.");
      setConfirmAll(false);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(errMsg(e, "Couldn't reset.")); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="fallback-overrides-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-['Space_Grotesk'] text-xl font-semibold">When service stock runs out</h3>
          <p className="text-sm text-muted-foreground">Ingredients whose “When service stock runs out” setting is currently <b>overridden</b> — usually because a manager tapped <b>Approve</b> on a fallback request during service. Reset them here at the end of the day so tomorrow starts on the business default ({data?.business_default_label || (data ? (data.business_fallback_enabled ? "Service stock first, then fallback" : "no fallback — shortfall booked") : "…")}).</p>
        </div>
        {canManage ? (
          <Button className="h-12 rounded-xl" variant={approvalCount ? "default" : "outline"} disabled={!approvalCount || !!busy} onClick={() => setConfirmAll(true)} data-testid="fallback-overrides-reset-all">
            <RotateCcw className="h-4 w-4" /> Reset all from approvals{approvalCount ? ` (${approvalCount})` : ""}
          </Button>
        ) : null}
      </div>

      <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist">
        {[["all", `All overrides (${(data?.overrides || []).length})`], ["approval", `From approvals (${approvalCount})`], ["manual", `Set manually (${(data?.overrides || []).length - approvalCount})`]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} className={`h-10 rounded-lg px-3 text-sm font-medium ${filter === k ? "bg-secondary" : "text-muted-foreground hover:text-foreground"}`} data-testid={`fallback-overrides-filter-${k}`}>{l}</button>
        ))}
      </div>

      {error ? <p className="rounded-2xl border border-destructive/40 bg-destructive/10 p-3 text-sm" data-testid="fallback-overrides-error">{error} <Button variant="link" className="h-auto p-0 text-sm" onClick={load}>Retry</Button></p> : null}

      {data === null ? <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div> : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center" data-testid="fallback-overrides-empty">
          <ShieldCheck className="mx-auto h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          <p className="mt-3 font-semibold">{filter === "approval" ? "No approval-driven overrides" : "Every ingredient follows the business default"}</p>
          <p className="mt-1 text-sm text-muted-foreground">{filter === "approval" ? "Nothing was switched via Approve today — nothing to reset." : "Overrides made with Approve (or set on an ingredient) will show up here so you can reset them at the end of the day."}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="fallback-overrides-list">
          {rows.map((r) => (
            <Card key={r.id} className="rounded-2xl border-border bg-card" data-testid={`fallback-override-${r.id}`}>
              <CardContent className="flex flex-wrap items-center gap-3 p-4">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${r.reason === "approval" ? "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]" : "bg-secondary"}`}>
                  {r.reason === "approval" ? <Sparkles className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-base font-semibold">{r.name}</p>
                    <Badge className={`rounded-full ${POLICY_TONE[r.policy] || ""}`} data-testid={`fallback-override-policy-${r.id}`}>{POLICY_SHORT[r.policy] || r.policy_label}</Badge>
                    <Badge variant="outline" className="rounded-full text-[10px] capitalize">{r.category}</Badge>
                    {r.reason === "approval" ? <Badge variant="outline" className="rounded-full border-[hsl(var(--accent))]/40 text-[10px] text-[hsl(var(--accent))]">via Approve</Badge> : <Badge variant="outline" className="rounded-full text-[10px]">set manually</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {r.set_at ? `Switched ${when(r.set_at)}` : "Switched"}{r.set_by ? ` by ${r.set_by}` : ""}
                    {r.reason === "approval" ? ` · ${r.menu_item_name ? `${r.menu_item_name} · ` : ""}Table ${r.table_number || "—"}${r.staff_name ? ` · asked by ${r.staff_name}` : ""}` : ""}
                    {` · was: ${r.previous_policy_label || "Business default"}`}
                  </p>
                  <p className="text-xs text-muted-foreground">Service stock <b className="text-foreground">{fmt(r.stock_on_hand)} {r.unit}</b> · back of house <b className="text-foreground">{fmt(r.back_of_house_stock)} {r.unit}</b></p>
                </div>
                {canManage ? (
                  <Button variant="outline" className="h-11 rounded-xl" disabled={!!busy} onClick={() => reset([r.id], `${r.name} reset`)} data-testid={`fallback-override-reset-${r.id}`}>
                    {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reset to business default
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={confirmAll} onOpenChange={setConfirmAll}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="fallback-overrides-reset-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Reset approval overrides?</DialogTitle>
            <DialogDescription>{approvalCount} ingredient{approvalCount === 1 ? "" : "s"} switched to “Service stock first, then fallback” via Approve will go back to the business default. Policies set manually on an ingredient are not touched. This is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="h-12 rounded-xl" onClick={() => setConfirmAll(false)} data-testid="fallback-overrides-reset-all-cancel">Cancel</Button>
            <Button className="h-12 rounded-xl" disabled={busy === "all"} onClick={() => reset(null, "End-of-day reset")} data-testid="fallback-overrides-reset-all-confirm">{busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reset {approvalCount}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FallbackOverridesPanel;
